import React, { useState, useEffect, useCallback, useRef } from 'react';
import { theme, globalStyles } from './theme';
import { Button, SidebarItem } from './components/ui';
import Icons from './components/Icons';
import SplitPane from './components/SplitPane';
import RightPanel from './components/RightPanel';
import SaveDialog from './components/SaveDialog';
import SavedQueriesList from './components/SavedQueriesList';
import HistoryList from './components/HistoryList';
import ConnectionPanel from './components/ConnectionPanel';
import DataDictionary from './components/DataDictionary';
import TabBar from './components/TabBar';
import BindParamModal from './components/BindParamModal';
import { detectBindParams, applyBindParams } from './utils/bindParams';
import {
  resolveConnection, credentialsFor, connectionKey as connKeyOf,
  usesOraclePreflight, usesDataDictionary, isUsable,
} from './utils/connectionRouting';
import { useConfig } from './context/ConfigContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import AdminPage from './pages/AdminPage';
import AccountModal from './pages/AccountModal';
import SubscriptionRequiredPage from './pages/SubscriptionRequiredPage';
import PricingPage from './pages/PricingPage';
import AuthGate from './components/AuthGate';
import { apiPost, apiPut, apiDelete, setOn402Handler } from './api';
import { collection, onSnapshot, orderBy, query as fsQuery } from 'firebase/firestore';
import { firebaseDb } from './firebase';

const QUERIES_STORAGE_KEY    = 'fq-saved-queries';
const HISTORY_STORAGE_KEY    = 'fq-history';
const TABS_STORAGE_KEY       = 'fq-tabs';
const ACTIVE_TAB_KEY         = 'fq-active-tab';
const AI_QUERIES_STORAGE_KEY = 'fq-ai-queries';

// In auth mode, partition local-only state by uid so signing out + signing in
// as a different user on the same browser doesn't leak the previous user's
// tabs / history / AI prompts. In non-auth mode, fall back to the legacy
// unsuffixed keys (single-user behavior unchanged).
function uidKey(base, uid) {
  return uid ? `${base}:${uid}` : base;
}

// One-time migration: copy data from old 'bip-sql-*' keys to new 'fq-*' keys
;[
  ['bip-sql-saved-queries', 'fq-saved-queries'],
  ['bip-sql-history',       'fq-history'],
  ['bip-sql-tabs',          'fq-tabs'],
  ['bip-sql-active-tab',    'fq-active-tab'],
].forEach(([oldKey, newKey]) => {
  try {
    const v = localStorage.getItem(oldKey);
    if (v !== null && localStorage.getItem(newKey) === null) localStorage.setItem(newKey, v);
    localStorage.removeItem(oldKey);
  } catch { /* ignore */ }
});

function loadFromStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return fallback;
}

function saveQueriesToStorage(queries) {
  try { localStorage.setItem(QUERIES_STORAGE_KEY, JSON.stringify(queries)); } catch { /* ignore */ }
}


// ============================================================================
// FusionQuery — Oracle Fusion Cloud SQL Runner
// ============================================================================

// ---------------------------------------------------------------------------
// Main App
// ---------------------------------------------------------------------------
function AppInner({ serverConfig = {} }) {
  const { config, setConfig } = useConfig();
  const { user, claims, signOut } = useAuth();
  const isAdmin = !!claims?.admin;
  const cloudMode = !!(serverConfig?.saasAuthEnabled && user);
  const [showAdmin, setShowAdmin]         = useState(false);
  const [showAccount, setShowAccount]     = useState(false);
  const [showPaywall, setShowPaywall]     = useState(false);

  // Register 402 handler once — shows paywall whenever any API call gets 402
  useEffect(() => {
    setOn402Handler(() => setShowPaywall(true));
  }, []);

  // Handle Stripe redirect back (?checkout=cancelled)
  // success case is handled by PricingPage → AuthGate.onSubscribed
  const [checkoutBanner, setCheckoutBanner] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const v = params.get('checkout');
    return v === 'cancelled' ? v : null;
  });
  useEffect(() => {
    if (checkoutBanner) {
      // Clean URL without reloading
      window.history.replaceState({}, '', window.location.pathname);
      const t = setTimeout(() => setCheckoutBanner(null), 6000);
      return () => clearTimeout(t);
    }
  }, [checkoutBanner]);
  const [sidebarSection, setSidebarSection] = useState('queries');

  // ── Editor tabs ──────────────────────────────────────────────────────────
  const emptyTabResults = { columns: [], rows: [], rawXml: '' };
  const { connections, activeConnectionId: defaultConnectionId, connectionStatuses } = useConfig(); // multi-connection

  // Per-uid storage keys: when SaaS auth is on, namespace by user uid so that
  // signing out and signing in as a different user starts with a clean slate.
  const storageUid = serverConfig?.saasAuthEnabled ? (user?.uid || null) : null;
  const tabsKey         = uidKey(TABS_STORAGE_KEY,       storageUid);
  const activeTabKey    = uidKey(ACTIVE_TAB_KEY,         storageUid);
  const historyKey      = uidKey(HISTORY_STORAGE_KEY,    storageUid);
  const aiQueriesKey    = uidKey(AI_QUERIES_STORAGE_KEY, storageUid);

  const [tabs, setTabs] = useState(() =>
    loadFromStorage(tabsKey, [{ id: '1', name: 'Query 1', sql: '' }])
      .map(t => ({ results: emptyTabResults, error: null, executionTime: null, loading: false, validating: false, ...t }))
  );
  const [activeTabId, setActiveTabId] = useState(() => {
    const saved = loadFromStorage(activeTabKey, null);
    const existing = loadFromStorage(tabsKey, [{ id: '1' }]);
    return (saved && existing.find(t => t.id === saved)) ? saved : existing[0]?.id || '1';
  });

  // Persist tabs on change (using current uid-namespaced key)
  useEffect(() => {
    try { localStorage.setItem(tabsKey, JSON.stringify(tabs)); } catch {}
  }, [tabs, tabsKey]);
  useEffect(() => {
    try { localStorage.setItem(activeTabKey, activeTabId); } catch {}
  }, [activeTabId, activeTabKey]);

  // When the signed-in user changes (sign out, sign in as someone else),
  // reload tab/history/AI state from the new uid's storage. Prevents data
  // leaking across user accounts on a shared browser.
  const lastStorageUidRef = useRef(storageUid);
  useEffect(() => {
    if (lastStorageUidRef.current === storageUid) return;
    lastStorageUidRef.current = storageUid;
    const reloadedTabs = loadFromStorage(tabsKey, [{ id: '1', name: 'Query 1', sql: '' }])
      .map(t => ({ results: emptyTabResults, error: null, executionTime: null, loading: false, validating: false, ...t }));
    setTabs(reloadedTabs);
    const savedActive = loadFromStorage(activeTabKey, null);
    setActiveTabId(
      (savedActive && reloadedTabs.find(t => t.id === savedActive))
        ? savedActive
        : reloadedTabs[0]?.id || '1'
    );
    setQueryHistory(loadFromStorage(historyKey, []));
    setAiQueries(loadFromStorage(aiQueriesKey, []));
  }, [storageUid]); // eslint-disable-line

  // Derive active-tab values
  const activeTab = tabs.find(t => t.id === activeTabId) || tabs[0];
  const sql           = activeTab?.sql           || '';
  const results       = activeTab?.results       || emptyTabResults;
  const error         = activeTab?.error         || null;
  const executionTime = activeTab?.executionTime || null;
  const loading       = activeTab?.loading       || false;
  const validating    = activeTab?.validating    || false;
  const aiPrompt      = activeTab?.aiPrompt      || null;
  const aiRetrying    = activeTab?.aiRetrying    || false;
  const sddContext    = activeTab?.sddContext    || [];
  const sddWarnings   = activeTab?.sddWarnings   || [];
  const tabSelection  = activeTab?.selection     || null;
  // Each tab tracks its own connectionId (falls back to the global active connection)
  const tabConnectionId = activeTab?.connectionId || defaultConnectionId;
  const tabConnection   = connections.find(c => c.id === tabConnectionId) || connections[0] || null;
  const tabConfig       = { baseUrl: tabConnection?.baseUrl || '', username: tabConnection?.username || '', password: tabConnection?.password || '' };

  const setSql = useCallback((newSql) => {
    setTabs(prev => prev.map(t => t.id === activeTabId ? { ...t, sql: newSql } : t));
  }, [activeTabId]);

  // Helper: update fields on a specific tab
  const updateTab = useCallback((tabId, fields) => {
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, ...fields } : t));
  }, []);

  const clearQuery = useCallback(() => {
    updateTab(activeTabId, { sql: '', results: emptyTabResults, error: null, executionTime: null });
  }, [activeTabId, updateTab]); // eslint-disable-line

  // Tab CRUD
  const addTab = useCallback(() => {
    const id = String(Date.now());
    setTabs(prev => [...prev, { id, name: `Query ${prev.length + 1}`, sql: '', connectionId: defaultConnectionId }]);
    setActiveTabId(id);
  }, [defaultConnectionId]);

  const addTabWithSql = useCallback((sql, name, extraFields = {}) => {
    const id = String(Date.now());
    setTabs(prev => [...prev, { id, name: name || `Query ${prev.length + 1}`, sql, connectionId: defaultConnectionId, ...extraFields }]);
    setActiveTabId(id);
  }, [defaultConnectionId]);

  const closeTab = useCallback((tabId) => {
    setTabs(prev => {
      const next = prev.filter(t => t.id !== tabId);
      if (next.length === 0) {
        // Last tab closed — open a fresh empty one
        const newId = String(Date.now());
        setActiveTabId(newId);
        return [{ id: newId, name: 'Query 1', sql: '', connectionId: defaultConnectionId }];
      }
      if (tabId === activeTabId) {
        const idx = prev.findIndex(t => t.id === tabId);
        setActiveTabId(next[Math.min(idx, next.length - 1)].id);
      }
      return next;
    });
  }, [activeTabId, defaultConnectionId]);

  const renameTab = useCallback((tabId, name) => {
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, name } : t));
  }, []);

  const reorderTabs = useCallback((fromId, toId) => {
    setTabs(prev => {
      const from = prev.findIndex(t => t.id === fromId);
      const to   = prev.findIndex(t => t.id === toId);
      if (from === -1 || to === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }, []);
  // ─────────────────────────────────────────────────────────────────────────

  const [queries, setQueries] = useState(() => cloudMode ? [] : loadFromStorage(QUERIES_STORAGE_KEY, []));
  const [queriesCloudLoaded, setQueriesCloudLoaded] = useState(false);
  const queriesMigratingRef = useRef(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [queryHistory, setQueryHistory] = useState(() => loadFromStorage(historyKey, []));
  const [aiQueries, setAiQueries] = useState(() => loadFromStorage(aiQueriesKey, []));
  const [maxRows, setMaxRows] = useState('200');
  const [bindParamModal, setBindParamModal] = useState(null); // { params, values }
  const bindParamValuesRef = useRef({});                      // persists last-entered values
  const [rightPanel, setRightPanel] = useState(null); // null | 'schema' | 'generate'
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(260);
  const [sidebarDragging, setSidebarDragging] = useState(false);
  const sidebarDragStartX = useRef(0);
  const sidebarDragStartWidth = useRef(0);

  useEffect(() => {
    if (!sidebarDragging) return;
    const handleMouseMove = (e) => {
      const delta = e.clientX - sidebarDragStartX.current;
      const newWidth = Math.max(200, Math.min(500, sidebarDragStartWidth.current + delta));
      setSidebarWidth(newWidth);
    };
    const handleMouseUp = () => {
      setSidebarDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [sidebarDragging]);
  const [schema, setSchema] = useState([]);
  const [schemaLoading, setSchemaLoading] = useState(false);
  // Identity of the connection the current schema came from (null = none).
  const schemaLoadedForRef = useRef(null);

  // Real-time saved queries sync via Firestore onSnapshot
  useEffect(() => {
    if (!cloudMode || !user?.uid) return;

    setQueries([]);
    setQueriesCloudLoaded(false);
    queriesMigratingRef.current = false;

    let isFirstSnapshot = true;

    const q = fsQuery(
      collection(firebaseDb, 'userSavedQueries', user.uid, 'queries'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const remote = snapshot.docs.map(doc => {
        const d = doc.data();
        return {
          id:          doc.id,
          name:        d.name        || '',
          description: d.description || '',
          sql:         d.sql         || '',
          category:    d.category    || '',
          createdAt:   d.createdAt?.toDate?.() || d.createdAt,
          updatedAt:   d.updatedAt?.toDate?.() || d.updatedAt,
        };
      });

      if (isFirstSnapshot && remote.length === 0 && !queriesMigratingRef.current) {
        // Migrate from localStorage on first load if cloud is empty
        const local = loadFromStorage(QUERIES_STORAGE_KEY, []);
        if (local.length > 0) {
          queriesMigratingRef.current = true;
          for (const q of local) {
            try {
              await apiPost('/queries', {
                name:        q.name        || 'Query',
                description: q.description || '',
                sql:         q.sql         || '',
                category:    q.category    || '',
              });
            } catch (e) {
              console.warn('Migration: failed to migrate query', q.name, e);
            }
          }
          try { localStorage.removeItem(QUERIES_STORAGE_KEY); } catch {}
          // onSnapshot will fire again with migrated data
          return;
        }
      }

      setQueries(remote);
      if (isFirstSnapshot) {
        setQueriesCloudLoaded(true);
        isFirstSnapshot = false;
      }
    }, (err) => {
      console.error('Firestore queries listener error:', err);
      setQueriesCloudLoaded(true);
    });

    return () => unsubscribe();
  }, [cloudMode, user?.uid]); // intentional: re-subscribe only when user or mode changes

  // Persist queries, history, and AI queries to localStorage whenever they change
  useEffect(() => { if (!cloudMode) saveQueriesToStorage(queries); }, [queries, cloudMode]);
  useEffect(() => {
    try { localStorage.setItem(historyKey, JSON.stringify(queryHistory)); } catch {}
  }, [queryHistory, historyKey]);
  useEffect(() => {
    try { localStorage.setItem(aiQueriesKey, JSON.stringify(aiQueries)); } catch {}
  }, [aiQueries, aiQueriesKey]);

  // Which connection the schema should describe. Includes the backend type so
  // switching between two connections that share a host still refetches.
  const connectionKey = connKeyOf({ type: config.connectionType, baseUrl: config.baseUrl, env: config.env, username: config.username });

  // Fetch schema
  const fetchSchema = useCallback(async () => {
    if (!config.baseUrl || !config.username) return;
    setSchemaLoading(true);
    // Clear any previous schema error so a successful retry doesn't leave a
    // stale "Failed to load database schema" banner on the tab.
    updateTab(activeTabId, { error: null });
    try {
      const result = await apiPost('/bip/schema', config);
      if (result.schema) {
        setSchema(result.schema);
        schemaLoadedForRef.current = connectionKey;
      }
    } catch (err) {
      console.error('Schema fetch error:', err);
      updateTab(activeTabId, { error: { message: 'Failed to load database schema: ' + err.message, errorType: 'schema_error' } });
    }
    setSchemaLoading(false);
  }, [config, activeTabId, updateTab]);

  // Re-fetch whenever the connection changes, not just on first connect. The
  // two backends have entirely different namespaces, so a carried-over schema
  // is not merely stale, it is from the wrong system.
  useEffect(() => {
    if (!isUsable(config)) return;
    if (schemaLoadedForRef.current === connectionKey) return;
    setSchema([]);          // drop the previous system's tables immediately
    fetchSchema();
  }, [connectionKey, config.baseUrl, config.username, config.password, fetchSchema]);

  // A tab may override the active connection, so anything that depends on the
  // backend must resolve it per tab rather than reading the global config.
  const connectionForTab = useCallback(
    (tabId) => resolveConnection({ tabs, connections, defaultConnectionId, tabId }),
    [tabs, connections, defaultConnectionId]);

  const executeQuery = useCallback(async (sqlToRun) => {
    const tabId = activeTabId; // capture at call time — user may switch tabs mid-run
    const currentTab = tabs.find(t => t.id === tabId) || tabs[0];
    const conn = connectionForTab(tabId);
    const creds = credentialsFor(conn);
    updateTab(tabId, { loading: true, error: null, results: emptyTabResults, executionTime: null });
    const startTime = Date.now();

    // Whether a generated query actually runs is the only real measure of
    // generation quality, so report it back. Fire-and-forget: telemetry must
    // never interfere with the query itself.
    const reportOutcome = (payload) => {
      if (!currentTab?.aiPrompt) return; // only AI-generated queries
      apiPost('/llm/outcome', {
        prompt: currentTab.aiPrompt,
        sql:    sqlToRun.trim(),
        origin: 'generate',
        ...payload,
      }).catch(() => {});
    };

    try {
      const limit = Number(maxRows);
      const result = await apiPost('/bip/run-sql', { sql: sqlToRun.trim(), maxRows: limit > 0 ? limit : 0, ...creds });
      const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
      if (result.error) {
        updateTab(tabId, { loading: false, error: {
          message:    result.error,
          errorType:  result.errorType  || 'unknown',
          oracleCode: result.oracleCode || null,
          detail:     result.detail     || null,
        }, executionTime: elapsed });
        reportOutcome({ success: false, oracleCode: result.oracleCode || null, errorType: result.errorType || 'unknown' });
      } else {
        updateTab(tabId, {
          loading: false,
          results: { columns: result.columns || [], rows: result.rows || [], rawXml: result.rawXml || '' },
          executionTime: elapsed,
          error: null,
        });
        reportOutcome({ success: true, rowCount: result.rows?.length || 0 });
        setQueryHistory((prev) => [
          { sql, timestamp: new Date().toISOString(), rowCount: result.rows?.length || 0 },
          ...prev.slice(0, 49),
        ]);
      }
    } catch (err) {
      const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
      updateTab(tabId, { loading: false, executionTime: elapsed, error: {
        message:    err.message    || 'Unexpected error',
        errorType:  err.errorType  || 'unknown',
        oracleCode: err.oracleCode || null,
        detail:     err.detail     || null,
      }});
      reportOutcome({ success: false, oracleCode: err.oracleCode || null, errorType: err.errorType || 'unknown' });
    }
  }, [activeTabId, sql, tabs, connectionForTab, maxRows, updateTab]);

  const runQuery = useCallback(async (sqlOverride = null) => {
    const tabId = activeTabId; // capture now — user may switch tabs mid-validation
    const sqlToRun = sqlOverride || sql;

    // Instant checks — no LLM needed
    if (!sqlToRun || !sqlToRun.trim()) {
      updateTab(tabId, { error: { message: 'Enter a SQL query to run.', errorType: 'unknown' } });
      return;
    }
    if (!/^\s*(SELECT|WITH)\b/i.test(sqlToRun.trim())) {
      updateTab(tabId, {
        error: {
          message:    'Query must begin with SELECT (or WITH for CTEs).',
          errorType:  'sql_syntax',
          oracleCode: null,
          detail:     '• Only SELECT queries are supported.\n  → DML statements are not allowed.',
        },
      });
      return;
    }

    // LLM pre-flight validation — Oracle only. A WMS query is validated by the
    // compiler on the server, which is deterministic, free, and knows the
    // subset exactly; running it past an Oracle syntax checker first only
    // produces false rejections.
    const runConn = connectionForTab(tabId);
    if (!usesOraclePreflight(runConn)) {
      const wmsParams = detectBindParams(sqlToRun);
      if (wmsParams.length > 0) {
        setBindParamModal({ sql: sqlToRun, params: wmsParams, values: { ...bindParamValuesRef.current } });
        return;
      }
      executeQuery(sqlToRun);
      return;
    }

    updateTab(tabId, { validating: true, error: null });
    try {
      const result = await apiPost('/llm/validate-sql', { sql: sqlToRun });
      if (!result.valid && result.errors?.length) {
        updateTab(tabId, {
          validating: false,
          error: {
            message:    result.errors.join('\n'),
            errorType:  'sql_syntax',
            oracleCode: null,
            detail:     result.errors.map(e => `• ${e}`).join('\n\n'),
          },
        });
        return;
      }
    } catch {
      // LLM unavailable — fail-open, proceed to execute
    } finally {
      updateTab(tabId, { validating: false });
    }

    const params = detectBindParams(sqlToRun);
    if (params.length > 0) {
      setBindParamModal({ sql: sqlToRun, params, values: { ...bindParamValuesRef.current } });
      return;
    }
    executeQuery(sqlToRun);
  }, [activeTabId, sql, updateTab, executeQuery, connectionForTab]);

  const handleBindParamRun = useCallback((values) => {
    bindParamValuesRef.current = { ...bindParamValuesRef.current, ...values };
    const sqlForRun = bindParamModal?.sql || sql;
    setBindParamModal(null);
    const substituted = applyBindParams(sqlForRun, values);
    executeQuery(substituted);
  }, [bindParamModal, sql, executeQuery]);

  const saveQuery = async (queryData) => {
    const existingId = activeTab?.savedQueryId;
    if (existingId) {
      // Update — fire and forget; onSnapshot will update the queries list
      if (cloudMode) {
        apiPut(`/queries/${existingId}`, queryData).catch(console.error);
      } else {
        setQueries((prev) => prev.map((q) =>
          q.id === existingId
            ? { ...q, ...queryData, updatedAt: new Date().toISOString() }
            : q
        ));
      }
      updateTab(activeTabId, { savedSql: queryData.sql, name: queryData.name });
    } else {
      if (cloudMode) {
        try {
          const created = await apiPost('/queries', {
            name:        queryData.name        || 'Query',
            description: queryData.description || '',
            sql:         queryData.sql         || '',
            category:    queryData.category    || '',
          });
          // onSnapshot updates the queries list; we only need the ID for the tab link
          if (created) {
            updateTab(activeTabId, { savedQueryId: created.id, savedSql: queryData.sql, name: queryData.name });
          }
        } catch (e) {
          console.error('saveQuery (cloud):', e);
        }
      } else {
        // localStorage mode
        const saved = {
          ...queryData,
          id: String(Date.now()),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setQueries((prev) => [...prev, saved]);
        updateTab(activeTabId, { savedQueryId: saved.id, savedSql: queryData.sql, name: queryData.name });
      }
    }
    setShowSaveDialog(false);
  };
  const deleteQuery = (id) => {
    if (cloudMode) {
      apiDelete(`/queries/${id}`).catch(console.error);
      // onSnapshot will remove it from the queries list
    } else {
      setQueries((prev) => prev.filter((q) => q.id !== id));
    }
    // Unlink any tabs referencing this saved query (always needed)
    setTabs((prev) => prev.map((t) => t.savedQueryId === id ? { ...t, savedQueryId: null, savedSql: null } : t));
  };
  const reorderQueries = useCallback((fromId, toId) => {
    setQueries((prev) => {
      const from = prev.findIndex(q => q.id === fromId);
      const to   = prev.findIndex(q => q.id === toId);
      if (from === -1 || to === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }, []);
  const deleteHistory = (timestamp) => {
    setQueryHistory((prev) => prev.filter((h) => h.timestamp !== timestamp));
  };
  const loadQuery = (query) => {
    // If the query is already open in a tab, switch to it
    const existingTab = tabs.find((t) => t.savedQueryId === query.id);
    if (existingTab) { setActiveTabId(existingTab.id); return; }
    // Otherwise open a new tab linked to this saved query
    addTabWithSql(query.sql || '', query.name, { savedQueryId: query.id, savedSql: query.sql || '' });
  };
  const loadFromHistory = (h) => {
    setSql(h.sql || '');
  };

  const saveAiQuery = (title, prompt, sql) => {
    const item = { id: String(Date.now()), title, prompt, sql, createdAt: new Date().toISOString() };
    setAiQueries((prev) => [item, ...prev]);
  };
  const deleteAiQuery = (id) => setAiQueries((prev) => prev.filter((q) => q.id !== id));
  const loadAiQuery = (item) => addTabWithSql(item.sql || '', item.title || 'AI Query', { aiPrompt: item.prompt });

  // A refinement replaces the query in the current tab — the point is to iterate
  // on it, not to accumulate a tab per edit. Stale results are cleared so the
  // grid never shows output from the previous version of the query.
  const applyRefinedSql = useCallback((refinedSql, title) => {
    updateTab(activeTabId, {
      sql:           refinedSql,
      error:         null,
      results:       emptyTabResults,
      executionTime: null,
      ...(title ? { name: title } : {}),
    });
  }, [activeTabId, updateTab]);

  // Retry-with-AI: resubmit a failed SQL with the Oracle error as correction context
  const retryAiSql = useCallback(async (tabId) => {
    const t = tabs.find(x => x.id === tabId);
    if (!t || !t.aiPrompt || !t.error) return;
    updateTab(tabId, { aiRetrying: true });
    try {
      const result = await apiPost('/llm/retry-sql', {
        prompt: t.aiPrompt,
        sql:    t.sql,
        error:  {
          oracleCode: t.error.oracleCode || null,
          message:    t.error.message    || '',
          detail:     t.error.detail     || '',
        },
        schema: schema || [],
      });
      updateTab(tabId, {
        aiRetrying: false,
        sql:         result.sql,
        error:       null,
        results:     emptyTabResults,
        executionTime: null,
        sddContext:  result.sddContext  || [],
        sddWarnings: result.sddWarnings || [],
      });
    } catch (err) {
      updateTab(tabId, {
        aiRetrying: false,
        error: {
          message:    'Retry with AI failed: ' + (err.message || 'unknown error'),
          errorType:  'server_error',
          oracleCode: null,
          detail:     null,
        },
      });
    }
  }, [tabs, schema, updateTab]);

  const dismissSddForTab = useCallback((tabId) => {
    updateTab(tabId, { sddContext: [], sddWarnings: [] });
  }, [updateTab]);

  const { activeConnection } = useConfig();
  const activeConnStatus = defaultConnectionId ? (connectionStatuses[defaultConnectionId] ?? null) : null;
  const isConnected = config.baseUrl && config.username;

  // The Data Dictionary describes Oracle Fusion tables only — its catalog,
  // join graph and module docs have no counterpart in WMS. Showing it on a WMS
  // connection would hand back confident answers about tables that do not
  // exist on the pod, so it is hidden rather than left to fail.
  const showDataDictionary = usesDataDictionary({ type: config.connectionType });

  // Switching to a WMS connection while the dictionary is open would strand the
  // user on a panel that no longer applies.
  useEffect(() => {
    if (!showDataDictionary && sidebarSection === 'dictionary') setSidebarSection('queries');
  }, [showDataDictionary, sidebarSection]);

  return (
    <>
      <style>{globalStyles}</style>
      <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

        {/* ====== LEFT SIDEBAR ====== */}
        {sidebarOpen ? (
          <div style={{
            width: `${sidebarWidth}px`, background: theme.surface,
            display: 'flex', flexShrink: 0, overflow: 'hidden',
          }}>
            {/* Sidebar content column */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

              {/* Branding header — 46px, matches main header height */}
              <div style={{
                height: '46px', padding: '0 16px', flexShrink: 0,
                borderBottom: `1px solid ${theme.border}`,
                display: 'flex', alignItems: 'center', gap: '10px',
                background: theme.surface,
              }}>
                <img src="/logo.png" alt="FusionQuery" style={{ height: 28, width: 'auto', flexShrink: 0 }} />
                <div style={{ flex: 1, fontWeight: 700, fontSize: '15px' }}>FusionQuery</div>
                <button onClick={() => setSidebarOpen(false)}
                  style={{ background: 'none', border: 'none', color: theme.textMuted, cursor: 'pointer', padding: '4px', borderRadius: '4px' }}
                  title="Collapse sidebar"
                >
                  <Icons.ChevronRight style={{ transform: 'rotate(180deg)' }} />
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
              </div>

              {/* Nav */}
              <div style={{ padding: '8px 8px 4px' }}>
                <SidebarItem icon={<Icons.Code />} label="Saved Queries" active={sidebarSection === 'queries'}
                  onClick={() => setSidebarSection('queries')} badge={queries.length} />
                <SidebarItem icon={<Icons.Clock />} label="History" active={sidebarSection === 'history'}
                  onClick={() => setSidebarSection('history')} badge={queryHistory.length} />
                <SidebarItem icon={<Icons.Settings />} label="Connections" active={sidebarSection === 'settings'}
                  onClick={() => setSidebarSection('settings')} />
                {showDataDictionary && (
                  <SidebarItem icon={<Icons.BookOpen />} label="Data Dictionary" active={sidebarSection === 'dictionary'}
                    onClick={() => setSidebarSection('dictionary')} />
                )}
              </div>

              <div style={{ height: '1px', background: theme.border, margin: '4px 12px' }} />

              {/* Sidebar content */}
              <div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
                {sidebarSection === 'queries' && (
                  <SavedQueriesList
                    queries={queries}
                    onSelect={loadQuery}
                    onDelete={deleteQuery}
                    onReorder={reorderQueries}
                    openSavedQueryIds={new Set(tabs.map((t) => t.savedQueryId).filter(Boolean))}
                  />
                )}
                {sidebarSection === 'history' && (
                  <HistoryList history={queryHistory} onSelect={loadFromHistory} onDelete={deleteHistory} />
                )}
                {sidebarSection === 'settings' && (
                  <ConnectionPanel />
                )}
              </div>

              {/* Connection status footer */}
              <div style={{
                padding: '10px 14px', borderTop: `1px solid ${theme.border}`,
                display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px',
              }}>
                <div style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: activeConnStatus === 'ok'      ? theme.success
                            : activeConnStatus === 'fail'    ? theme.danger
                            : activeConnStatus === 'testing' ? theme.warning
                            : isConnected ? theme.success : theme.danger,
                  boxShadow: activeConnStatus === 'ok'   ? `0 0 6px ${theme.success}99`
                           : activeConnStatus === 'fail' ? `0 0 6px ${theme.danger}99`
                           : 'none',
                  transition: 'background 0.3s, box-shadow 0.3s',
                }} />
                <span style={{ color: theme.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, maxWidth: '140px' }}>
                  {isConnected
                    ? (activeConnection?.name || 'Connected')
                    : 'Not configured'}
                </span>
              </div>
            </div>

            {/* Drag handle — right edge */}
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                sidebarDragStartX.current = e.clientX;
                sidebarDragStartWidth.current = sidebarWidth;
                setSidebarDragging(true);
                document.body.style.cursor = 'col-resize';
                document.body.style.userSelect = 'none';
              }}
              style={{
                width: '5px', flexShrink: 0, cursor: 'col-resize',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: sidebarDragging ? `${theme.accent}22` : 'transparent',
                borderRight: `1px solid ${theme.border}`,
                transition: sidebarDragging ? 'none' : 'background 0.15s',
              }}
              onMouseEnter={(e) => { if (!sidebarDragging) e.currentTarget.style.background = `${theme.accent}11`; }}
              onMouseLeave={(e) => { if (!sidebarDragging) e.currentTarget.style.background = 'transparent'; }}
            />
          </div>
        ) : (
          /* Collapsed sidebar — thin strip */
          <div style={{
            width: '40px', flexShrink: 0, background: theme.surface,
            borderRight: `1px solid ${theme.border}`,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
          }}>
            {/* Header cell — 46px, holds expand button */}
            <div style={{
              height: '46px', width: '100%', flexShrink: 0,
              borderBottom: `1px solid ${theme.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <button onClick={() => setSidebarOpen(true)}
                style={{ background: 'none', border: 'none', color: theme.textMuted, cursor: 'pointer', padding: '6px', borderRadius: '6px' }}
                title="Expand sidebar"
                onMouseEnter={(e) => (e.currentTarget.style.background = theme.surfaceHover)}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
            {/* Connection dot */}
            <div style={{ marginTop: '12px', width: '8px', height: '8px', borderRadius: '50%', background: isConnected ? theme.success : theme.danger }} />
          </div>
        )}

        {/* ====== MAIN CONTENT ====== */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Main header — 46px, aligns with sidebar branding header */}
          <div style={{
            height: '46px', padding: '0 16px', flexShrink: 0,
            borderBottom: `1px solid ${theme.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: theme.surface,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '14px', fontWeight: 600 }}>Query Editor</span>
              {executionTime && !loading && results.columns.length > 0 && (
                <span style={{ fontSize: '12px', color: theme.textMuted }}>{executionTime}s</span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Button variant={rightPanel === 'schema' ? 'primary' : 'outline'} size="sm" icon={<Icons.Database />}
                onClick={() => setRightPanel(p => p === 'schema' ? null : 'schema')}>
                Schema
              </Button>
              <Button variant={rightPanel === 'generate' ? 'primary' : 'outline'} size="sm" icon={<Icons.Zap />}
                onClick={() => setRightPanel(p => p === 'generate' ? null : 'generate')}>
                Generate
              </Button>
              {/* Account button (only when auth is active) */}
              {user && (
                <button onClick={() => setShowAccount(true)} title={`Signed in as ${user.email}`}
                  style={{
                    background: 'none', border: `1px solid ${theme.border}`, borderRadius: '6px',
                    color: theme.textMuted, fontSize: '12px', padding: '0 8px', height: 28,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                    maxWidth: 200, overflow: 'hidden',
                  }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.email?.split('@')[0]}
                  </span>
                  <span style={{ opacity: 0.5, flexShrink: 0 }}>▾</span>
                </button>
              )}
              {isAdmin && (
                <button onClick={() => setShowAdmin(true)} title="Admin dashboard"
                  style={{
                    background: 'none', border: `1px solid ${theme.accent}66`, borderRadius: '6px',
                    color: theme.accent, fontSize: '12px', padding: '0 8px', height: 28, cursor: 'pointer',
                  }}>
                  Admin
                </button>
              )}
              <a
                href="/help.html"
                target="_blank"
                rel="noopener noreferrer"
                title="User Manual"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 28, height: 28, borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  color: theme.textMuted, fontSize: '13px', fontWeight: 700,
                  textDecoration: 'none', flexShrink: 0,
                  transition: 'color 0.15s, border-color 0.15s, background 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = theme.accent; e.currentTarget.style.borderColor = theme.accent; e.currentTarget.style.background = `${theme.accent}18`; }}
                onMouseLeave={e => { e.currentTarget.style.color = theme.textMuted; e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.background = 'transparent'; }}
              >?</a>
            </div>
          </div>

          {/* Data Dictionary portal — replaces editor when active */}
          {sidebarSection === 'dictionary' && showDataDictionary && (
            <DataDictionary
              onClose={() => setSidebarSection('queries')}
              onOpenInEditor={addTabWithSql}
            />
          )}
          {(sidebarSection !== 'dictionary' || !showDataDictionary) && <React.Fragment>

          {/* Tab bar */}
          <TabBar
            tabs={tabs}
            activeTabId={activeTabId}
            onTabSelect={setActiveTabId}
            onTabAdd={addTab}
            onTabClose={closeTab}
            onTabRename={renameTab}
            onTabReorder={reorderTabs}
            onTabSave={() => setShowSaveDialog(true)}
            canSave={!!sql}
          />

          {/* Editor + Results with draggable divider */}
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            <SplitPane
              activeTabId={activeTabId}
              sql={sql} setSql={setSql} onClearQuery={clearQuery} runQuery={runQuery} loading={loading} validating={validating}
              results={results} error={error}
              maxRows={maxRows} onMaxRowsChange={setMaxRows}
              savedSelection={tabSelection}
              onSelectionPersist={(selection) => updateTab(activeTabId, { selection })}
              schema={schema}
              connections={connections}
              tabConnectionId={tabConnectionId}
              onTabConnectionChange={(connId) => updateTab(activeTabId, { connectionId: connId })}
              aiPrompt={aiPrompt}
              aiRetrying={aiRetrying}
              sddContext={sddContext}
              sddWarnings={sddWarnings}
              onRetryAi={() => retryAiSql(activeTabId)}
              onDismissSdd={() => dismissSddForTab(activeTabId)}
            />

            {/* Right panel — Schema (DB Browser) or Generate SQL */}
            {rightPanel && (
              <RightPanel
                activeTab={rightPanel}
                onTabChange={setRightPanel}
                onClose={() => setRightPanel(null)}
                schema={schema}
                schemaLoading={schemaLoading}
                onRefresh={fetchSchema}
                onTableAction={addTabWithSql}
                aiQueries={aiQueries}
                onSave={saveAiQuery}
                onDelete={deleteAiQuery}
                onSelect={loadAiQuery}
                onGenerate={(sql, title, aiPrompt) => addTabWithSql(sql, title || 'AI Query', { aiPrompt })}
                currentSql={sql}
                currentAiPrompt={aiPrompt}
                onRefine={applyRefinedSql}
              />
            )}
          </div>
          </React.Fragment>}
        </div>
      </div>

      {/* Save Dialog */}
      {showSaveDialog && (
        <SaveDialog
          sql={sql}
          onSave={saveQuery}
          onClose={() => setShowSaveDialog(false)}
          initialData={queries.find((q) => q.id === activeTab?.savedQueryId)}
          defaultName={activeTab?.name}
          defaultDescription={activeTab?.aiPrompt}
        />
      )}

      {/* Bind Parameter Modal */}
      {bindParamModal && (
        <BindParamModal
          params={bindParamModal.params}
          initialValues={bindParamModal.values}
          onRun={handleBindParamRun}
          onClose={() => setBindParamModal(null)}
        />
      )}

      {/* Stripe checkout return banner */}
      {checkoutBanner && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 3000,
          background: checkoutBanner === 'success' ? '#16a34a' : theme.surface,
          border: `1px solid ${checkoutBanner === 'success' ? '#16a34a' : theme.border}`,
          color: '#fff', borderRadius: 10,
          padding: '12px 24px', fontSize: 14, fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          {checkoutBanner === 'success'
            ? '🎉 Subscription activated! Welcome to FusionQuery Pro.'
            : '👋 Checkout cancelled — you can subscribe any time.'}
          <button
            onClick={() => setCheckoutBanner(null)}
            style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16 }}
          >×</button>
        </div>
      )}

      {/* Account modal */}
      {showAccount && user && (
        <AccountModal onClose={() => setShowAccount(false)} />
      )}

      {/* Admin dashboard modal */}
      {showAdmin && isAdmin && (
        <AdminPage onClose={() => setShowAdmin(false)} />
      )}

      {/* Subscription paywall — shown on any 402 response */}
      {showPaywall && (
        serverConfig?.billingEnabled
          ? <PricingPage overlay onDismiss={() => setShowPaywall(false)} />
          : <SubscriptionRequiredPage onDismiss={() => setShowPaywall(false)} />
      )}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AuthGate AppComponent={AppInner} />
    </AuthProvider>
  );
}
